import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Button } from "./ui/button";

const STORAGE_KEY = "flowmindai-cookie-consent";

/**
 * RGPD-compliant cookie banner.
 * - Stores consent in localStorage as { essential: true, analytics: bool, ts }
 * - Exposes window.flowmindCookieConsent for analytics gating
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(true);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        setVisible(true);
      } else {
        const parsed = JSON.parse(raw);
        window.flowmindCookieConsent = parsed;
      }
    } catch (e) {
      setVisible(true);
    }
  }, []);

  const persist = (consent) => {
    const payload = { ...consent, ts: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    window.flowmindCookieConsent = payload;
    // Toggle GA4 if user refused analytics
    if (window.gtag) {
      window.gtag("consent", "update", {
        analytics_storage: consent.analytics ? "granted" : "denied",
      });
    }
    setVisible(false);
  };

  const acceptAll = () => persist({ essential: true, analytics: true });
  const refuseAll = () => persist({ essential: true, analytics: false });
  const savePrefs = () => persist({ essential: true, analytics });

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[60] p-4 md:p-6 pointer-events-none"
      data-testid="cookie-banner"
    >
      <div className="max-w-4xl mx-auto pointer-events-auto bg-white border border-zinc-200 shadow-2xl rounded-sm overflow-hidden">
        <div className="p-6 md:p-8">
          <div className="flex items-start gap-4">
            <div className="hidden md:flex w-10 h-10 klein-bg rounded-sm items-center justify-center flex-shrink-0">
              <Cookie size={20} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-display text-xl font-medium tracking-tight">
                  Cookies & vie privée
                </h3>
                <button
                  onClick={refuseAll}
                  className="text-zinc-400 hover:text-black transition-colors md:hidden"
                  aria-label="Refuser et fermer"
                  data-testid="cookie-close-mobile"
                >
                  <X size={18} />
                </button>
              </div>
              <p className="text-sm text-zinc-600 leading-relaxed mt-2">
                Nous utilisons des cookies <strong>strictement nécessaires</strong> au fonctionnement du service,
                et des cookies <strong>analytiques anonymisés</strong> (Google Analytics, Microsoft Clarity) pour
                améliorer l'expérience. Aucun cookie publicitaire ni de tracking tiers.
                {" "}
                <Link to="/privacy" className="underline hover:text-black" data-testid="cookie-privacy-link">
                  En savoir plus
                </Link>.
              </p>

              {showPrefs && (
                <div className="mt-5 space-y-3 border-t border-zinc-200 pt-5" data-testid="cookie-preferences">
                  <label className="flex items-start gap-3 cursor-not-allowed opacity-60">
                    <input
                      type="checkbox"
                      checked
                      disabled
                      className="mt-1 rounded-sm border-zinc-300"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium">Essentiels <span className="text-xs font-normal text-zinc-500">(toujours actifs)</span></p>
                      <p className="text-xs text-zinc-500">Authentification, sécurité, session — indispensables au service.</p>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={analytics}
                      onChange={(e) => setAnalytics(e.target.checked)}
                      className="mt-1 rounded-sm border-zinc-300"
                      data-testid="cookie-analytics-toggle"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium">Analytiques anonymisés</p>
                      <p className="text-xs text-zinc-500">GA4 + Microsoft Clarity — IP anonymisée, durée 13 mois max.</p>
                    </div>
                  </label>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 mt-5">
                <Button
                  onClick={acceptAll}
                  className="rounded-sm bg-black text-white hover:bg-zinc-800 h-10"
                  data-testid="cookie-accept-all"
                >
                  Tout accepter
                </Button>
                <Button
                  onClick={refuseAll}
                  variant="outline"
                  className="rounded-sm h-10"
                  data-testid="cookie-refuse-all"
                >
                  Tout refuser
                </Button>
                {showPrefs ? (
                  <Button
                    onClick={savePrefs}
                    variant="outline"
                    className="rounded-sm h-10"
                    data-testid="cookie-save-prefs"
                  >
                    Enregistrer mes choix
                  </Button>
                ) : (
                  <button
                    onClick={() => setShowPrefs(true)}
                    className="text-sm text-zinc-600 hover:text-black underline ml-2"
                    data-testid="cookie-open-prefs"
                  >
                    Personnaliser
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
